import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  alpha,
  useMediaQuery,
} from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useTranslation } from "react-i18next";
import { buildMessageEditVersions } from "../utils/teamEditHistoryUtils";
import { formatMessageDateTime, TEAM_MOBILE_MEDIA_QUERY } from "../utils/teamUiUtils";
import TeamLinkifiedText from "./TeamLinkifiedText";

const TeamMessageEditHistoryDialog = ({ open, onClose, message }) => {
  const { t } = useTranslation("team");
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);

  const versions = useMemo(() => buildMessageEditVersions(message), [message]);

  const kindLabel = (kind) => {
    if (kind === "current") return t("editHistoryCurrent");
    if (kind === "original") return t("editHistoryOriginal");
    return t("editHistoryRevision");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <HistoryOutlinedIcon fontSize="small" color="action" />
        {t("editHistoryTitle")}
      </DialogTitle>
      <DialogContent dividers>
        {message?.authorName ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {message.authorName}
          </Typography>
        ) : null}

        {!versions.length ? (
          <Typography variant="body2" color="text.secondary">
            {t("editHistoryEmpty")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {versions.map((version, idx) => (
              <Box
                key={`${version.kind}-${idx}-${version.at || idx}`}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: version.kind === "current" ? "primary.main" : "divider",
                  bgcolor: (th) =>
                    version.kind === "current"
                      ? alpha(th.palette.primary.main, 0.06)
                      : th.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Chip
                    size="small"
                    label={kindLabel(version.kind)}
                    color={version.kind === "current" ? "primary" : "default"}
                    variant={version.kind === "current" ? "filled" : "outlined"}
                    sx={{ height: 22, fontSize: "0.6875rem" }}
                  />
                  {version.at ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatMessageDateTime(version.at)}
                    </Typography>
                  ) : null}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}
                >
                  <TeamLinkifiedText content={version.content} />
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("editHistoryClose")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMessageEditHistoryDialog;
